import { parseKey } from './keys';

// SDK BFF base URL per key tier. `data-config-endpoint` on the script tag
// overrides these (used by the test harness and local dev).
//
// Both tiers address the api-gateway. The functions' own hostnames keep
// answering, so a loader copy cached on a dealer page against the address it
// was built with continues to serve until that copy is replaced. Switching
// those hostnames off is a separate change, and it waits on their traffic
// draining rather than on this one.
const SDK_API_BASE_BY_TIER: Record<string, string> = {
  live: 'https://internal.ekho.com/sdk',
  test: 'https://staging-internal.ekho.com/sdk',
};

export interface ScriptConfig {
  key: string | null;
  debug: boolean;
  endpoint: string | null;
  nonce: string | null;
}

/**
 * Read loader configuration synchronously from the script tag. Uses
 * `querySelector('script[data-key]')` rather than `document.currentScript`
 * because the latter is null inside the async bootstrap continuation.
 */
export function readScriptConfig(): ScriptConfig {
  const el = document.querySelector<HTMLScriptElement>('script[data-key]');
  return {
    key: el?.dataset.key ?? null,
    debug: el?.dataset.debug === 'true',
    endpoint: el?.dataset.configEndpoint ?? null,
    nonce: el?.nonce ?? el?.getAttribute('nonce') ?? null,
  };
}

/**
 * Resolve the SDK BFF base URL from an explicit override or the key tier.
 *
 * Always a base, never a finished endpoint: callers build `/config`, `/cart`
 * and `/pv` by appending to it, and every one of those is mounted as a sibling
 * rather than nested under another. `data-config-endpoint` is a complete config
 * URL, so a trailing `/config` segment is dropped to recover the base it sits
 * under. Without that, appending produces a path one level too deep and the
 * request 404s.
 *
 * Only a trailing `/config` path segment is stripped. An override that names
 * config differently, such as the `/mock-config` the sdk-express harnesses use,
 * is left alone rather than guessed at.
 */
export function resolveApiBaseUrl(key: string, override: string | null): string | null {
  if (override) {
    // Falling back to the override keeps an override of exactly `/config` from
    // stripping down to an empty string, which reads as "unresolvable" to
    // callers and would deactivate the SDK rather than mis-route one request.
    const stripped = override.replace(/\/config\/?$/, '');
    return stripped === '' ? override : stripped;
  }
  const parsed = parseKey(key);
  return parsed ? (SDK_API_BASE_BY_TIER[parsed.tier] ?? null) : null;
}

/**
 * Resolve the config endpoint URL.
 *
 * An override is used verbatim rather than rebuilt from the base. It has to be:
 * the sdk-express harnesses point it at `/mock-config`, which the base resolver
 * deliberately leaves intact, so appending `/config` would ask for
 * `/mock-config/config` and find nothing.
 */
export function resolveEndpoint(key: string, override: string | null): string | null {
  const base = resolveApiBaseUrl(key, override);
  if (!base) return null;
  if (override) return override;
  return `${base}/config`;
}
